/**
 * Single source of the VR-headset User-Agent detection shared by Session
 * Management identification (`signalR.ts`) and streamer selection
 * (`isCloudXrClient.ts`), so the two can't drift out of sync.
 *
 * IMPORTANT: the Pico browser's User-Agent contains BOTH "PicoBrowser" and
 * "OculusBrowser" (it spoofs Oculus for compatibility), so any caller that
 * needs to tell Pico and Quest apart MUST check Pico first.
 */

const PICO_UA = /PicoBrowser|Pico Neo|PicoVR|\bPICO\b/i;
const QUEST_UA = /OculusBrowser/i;

export function isPicoBrowserUa(ua: string): boolean {
  return PICO_UA.test(ua);
}

export function isQuestBrowserUa(ua: string): boolean {
  return QUEST_UA.test(ua);
}

/** The Pico model from the UA platform token, e.g. "PICO 4 Enterprise". */
export function parsePicoModel(ua: string): string {
  return (
    ua.match(/\b(PICO\b[\w ]*?)\s*(?:OS\d|like\b|\))/i)?.[1]?.trim() ?? "Pico"
  );
}

/** The Quest model from the UA platform token, e.g. "Quest 3". */
export function parseQuestModel(ua: string): string {
  return ua.match(/;\s*(Quest[^;)]*)\)/i)?.[1]?.trim() ?? "Quest";
}

/** True when the current browser is a VR headset browser (real or emulator-spoofed). */
export function isVrHeadsetBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return isPicoBrowserUa(ua) || isQuestBrowserUa(ua);
}

/**
 * True when the Client Hints describe a desktop host. The Meta Immersive Web
 * Emulator overrides `navigator.userAgent` to a Quest string but leaves the
 * structured Client Hints reporting the real desktop browser (`mobile: false`
 * and a desktop `platform`), so this distinguishes an emulator-on-desktop from
 * a real headset. Pure so it can be unit-tested.
 *
 * We only match platforms a real headset can NEVER report. "Linux" is
 * deliberately EXCLUDED: a real Quest reports `platform: "Linux"` (its browser
 * runs on X11/Linux), so it can't tell a Quest apart from a Linux desktop —
 * matching it here mislabels real Quests as "(Emulator)". The trade-off is that
 * the emulator on a *Linux* desktop won't get the label (cosmetic, and rare —
 * most testers are on macOS/Windows).
 */
export function isDesktopClientHints(uaData?: {
  mobile?: boolean;
  platform?: string;
}): boolean {
  if (!uaData) return false;
  const desktopOnlyPlatforms = ["macOS", "Windows", "Chrome OS"];
  return (
    uaData.mobile === false &&
    !!uaData.platform &&
    desktopOnlyPlatforms.includes(uaData.platform)
  );
}

/** True when a desktop browser is spoofing a headset UA (e.g. the Meta emulator). */
export function isEmulatedHeadsetOnDesktop(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (
    navigator as Navigator & {
      userAgentData?: { mobile?: boolean; platform?: string };
    }
  ).userAgentData;
  return isDesktopClientHints(uaData);
}
